import type { QuoteDocument } from '@kalos/kalos-rpc';

// Fallback threshold that matches the backend's default LostThresholdDays in
// GetProposalMetrics / GetProposalsByStatus. Keep in sync with
// DEFAULT_LOST_THRESHOLD_DAYS in modules/Analytics/hooks/useProposalManagement.ts.
const LOST_FALLBACK_DAYS = 30;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

// MySQL zero-date sentinels returned by the backend when a datetime column is
// unset. Both the date-only ('0001-01-01') and datetime ('0001-01-01 00:00:00',
// '0000-00-00 00:00:00') forms show up in practice.
export function isValidDate(date: string | undefined | null): date is string {
  if (!date) return false;
  if (date.startsWith('0001-01-01')) return false;
  if (date.startsWith('0000-00-00')) return false;
  return true;
}

/**
 * Mirrors the backend's "lost" classification in v2/server/quote/metrics.go so
 * the per-job proposal card flips to the expired variant whenever the Proposal
 * Management list would show the proposal as Lost. A proposal is lost/expired
 * when it has not been signed AND any of:
 *   - the job's log_jobStatus is 'Canceled'
 *   - quote_expire_date is set and in the past
 *   - quote_sent_date was more than LOST_FALLBACK_DAYS ago
 */
export function isProposalExpired(
  quoteDocument: QuoteDocument | null | undefined,
  jobStatus?: string,
): boolean {
  if (!quoteDocument) return false;
  if (isValidDate(quoteDocument.signedDate)) return false;

  if (jobStatus === 'Canceled') return true;

  if (isValidDate(quoteDocument.quoteExpireDate)) {
    const expire = new Date(quoteDocument.quoteExpireDate);
    if (!Number.isNaN(expire.getTime()) && expire.getTime() < Date.now()) {
      return true;
    }
  }

  if (isValidDate(quoteDocument.quoteSentDate)) {
    const sent = new Date(quoteDocument.quoteSentDate);
    if (!Number.isNaN(sent.getTime())) {
      const daysSinceSent = (Date.now() - sent.getTime()) / MS_PER_DAY;
      if (daysSinceSent > LOST_FALLBACK_DAYS) return true;
    }
  }

  return false;
}
